<template>
  <div :class="$style.container">
    <div :class="$style.header">
      <div :class="$style.headerLeft">
        <button :class="$style.backButton" @click="goBack" title="뒤로가기">←</button>
        <h2 :class="$style.title">내 공연 달력</h2>
      </div>
      <button v-if="likedLives.length > 0" :class="$style.googleCalBtn" @click="showGoogleCalModal = true"
        title="구글 캘린더 연동">
        <span>구글 캘린더 연동</span>
      </button>
      <div v-else :class="$style.spacer"></div>
    </div>

    <!-- 구글 캘린더 연동 모달 -->
    <div v-if="showGoogleCalModal" :class="$style.modalOverlay" @click.self="showGoogleCalModal = false">
      <div :class="$style.modal">
        <div :class="$style.modalHeader">
          <span :class="$style.modalTitle">구글 캘린더 연동</span>
          <button :class="$style.modalClose" @click="showGoogleCalModal = false">✕</button>
        </div>
        <p :class="$style.modalDesc">공연을 선택해 구글 캘린더에 바로 추가하세요.</p>
        <div :class="$style.modalList">
          <div v-for="live in sortedLikedLives" :key="live.id" :class="$style.modalItem">
            <div :class="$style.modalItemInfo">
              <span :class="[$style.typeBadge, $style[`eventType__${live.type || 'default'}`]]">{{ live.type || '-'
                }}</span>
              <div :class="$style.modalItemText">
                <div :class="$style.modalItemTitle">{{ live.title }}</div>
                <div :class="$style.modalItemMeta">{{ live.date }} · {{ live.place }}</div>
              </div>
            </div>
            <a :href="googleCalUrl(live)" target="_blank" rel="noopener" :class="$style.addBtn">추가</a>
          </div>
        </div>
      </div>
    </div>

    <div v-if="loading" :class="$style.loading">
      <div :class="$style.spinner"></div>
      <p>찜한 공연을 불러오는 중...</p>
    </div>

    <div v-else-if="likedLives.length === 0" :class="$style.emptyState">
      <div :class="$style.emptyIcon">📅</div>
      <h3>찜한 공연이 없습니다</h3>
      <p>마음에 드는 공연을 찜해보세요!</p>
    </div>

    <div v-else :class="$style.calendarContainer">
      <!-- 달력 네비게이션 -->
      <div :class="$style.calendarHeader">
        <button :class="$style.navButton" @click="previousMonth">‹</button>
        <h2 :class="$style.monthTitle">{{ currentMonthText }}</h2>
        <button :class="$style.navButton" @click="nextMonth">›</button>
      </div>

      <!-- 달력 그리드 -->
      <div :class="$style.calendarWrapper">
        <!-- 요일 헤더 -->
        <div :class="$style.weekdayHeader">
          <div v-for="day in weekdays" :key="day" :class="$style.weekday">{{ day }}</div>
        </div>

        <!-- 날짜 셀 -->
        <div :class="$style.calendarGrid">
          <div v-for="(date, index) in calendarDays" :key="index" :class="[
            $style.calendarDay,
            { [$style.otherMonth]: !date.isCurrentMonth },
            { [$style.sunday]: date.dayOfWeek === 0 },
          ]" @click="selectDate(date)">
            <!-- 날짜 숫자 -->
            <div :class="[$style.dayNumberWrap, { [$style.todayBadge]: date.isToday }]">
              <span :class="$style.dayNumber">{{ date.day }}</span>
            </div>
            <!-- 포스터 썸네일 모음 -->
            <div v-if="date.events.length > 0" :class="$style.posterThumbs">
              <div v-for="(ev, i) in date.events" :key="ev.id || i"
                :class="[$style.posterThumb, { [$style.posterThumbEmpty]: !ev.poster }]"
                :style="ev.poster ? { backgroundImage: `url(${ev.poster})` } : {}" />
            </div>
            <!-- 이벤트 pill 목록 -->
            <div v-if="date.events.length > 0" :class="$style.calendarDayEvents">
              <div v-for="(ev, i) in date.events" :key="(ev.id || 't') + i"
                :class="[$style.calendarEventPill, $style[`eventType__${ev.type || 'default'}`]]" :title="ev.title">
                {{ ev.title || '제목 없음' }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 선택된 날짜의 공연 목록 -->
      <div v-if="selectedDateEvents.length > 0" :class="$style.selectedDateEvents">
        <h3 :class="$style.selectedDateTitle">{{ selectedDateText }} 공연</h3>
        <div :class="$style.eventsList">
          <PerformanceCard v-for="event in selectedDateEvents" :key="event.id" :performance="event"
            @click="goToLiveDetail" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useUserStore } from "@/store/user";
import {
  collection,
  getDocs,
  query,
  where,
  documentId,
} from "firebase/firestore";
import { ref as storageRef } from "firebase/storage";
import { getDownloadURL } from "@/utils/storageWithNavigation";
import { firestore, storage } from "/firebaseConfig";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addMonths, subMonths, isSameDay, parse, isValid } from "date-fns";
import { ko } from "date-fns/locale";
import PerformanceCard from "@/components/PerformanceCard.vue";
import { trackActivity, ACTIVITY_TYPES } from "@/utils/activityTracker";

export default {
  name: "MyCalendar",
  components: {
    PerformanceCard,
  },
  data() {
    return {
      userStore: useUserStore(),
      loading: true,
      likedLives: [],
      userLiveLikes: [],
      currentDate: new Date(),
      selectedDate: null,
      showGoogleCalModal: false,
    };
  },
  computed: {
    currentMonthText() {
      return format(this.currentDate, "yyyy년 M월", { locale: ko });
    },
    weekdays() {
      return ["일", "월", "화", "수", "목", "금", "토"];
    },
    calendarDays() {
      const monthStart = startOfMonth(this.currentDate);
      const monthEnd = endOfMonth(this.currentDate);
      const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 });
      const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });

      const days = [];
      let currentDay = new Date(calendarStart);

      while (currentDay <= calendarEnd) {
        const dayEvents = this.getEventsForDate(currentDay);
        const isCurrentMonth = currentDay.getMonth() === this.currentDate.getMonth();
        const isToday = isSameDay(currentDay, new Date());

        days.push({
          date: new Date(currentDay),
          day: currentDay.getDate(),
          dayOfWeek: currentDay.getDay(),
          isCurrentMonth,
          isToday,
          hasEvent: dayEvents.length > 0,
          events: dayEvents,
        });

        currentDay.setDate(currentDay.getDate() + 1);
      }

      return days;
    },
    selectedDateEvents() {
      if (!this.selectedDate) return [];
      return this.getEventsForDate(this.selectedDate);
    },
    selectedDateText() {
      if (!this.selectedDate) return "";
      return format(this.selectedDate, "M월 d일", { locale: ko });
    },
    sortedLikedLives() {
      return [...this.likedLives].sort((a, b) => {
        const parseD = (s) => {
          if (!s) return 0;
          const t = s.replace(/\./g, '-').trim();
          const f = t.includes(':') ? t : t + ' 00:00';
          const d = parse(f, 'yyyy-MM-dd HH:mm', new Date());
          return isValid(d) ? d.getTime() : 0;
        };
        return parseD(b.date) - parseD(a.date);
      });
    },
  },
  watch: {
    currentDate() {
      this.$nextTick(() => this.resolveLikedLivePosters());
    },
  },
  async mounted() {
    // 사용자 정보 동기화
    if (this.userStore.isLogin) {
      await this.userStore.syncUserData();
    }

    await this.loadUserLikes();
    await this.loadLikedLives();
    this.loading = false;
  },
  methods: {
    goBack() {
      this.$router.back();
    },
    previousMonth() {
      this.currentDate = subMonths(this.currentDate, 1);
      this.selectedDate = null;
    },
    nextMonth() {
      this.currentDate = addMonths(this.currentDate, 1);
      this.selectedDate = null;
    },
    selectDate(dateObj) {
      if (dateObj.hasEvent) {
        // 공연이 있는 날짜를 클릭하면 선택
        this.selectedDate = dateObj.date;
      } else {
        // 공연이 없는 날짜를 클릭하면 선택 해제
        this.selectedDate = null;
      }
    },
    getEventsForDate(date) {
      return this.likedLives.filter((live) => {
        const liveDate = this.parseLiveEventDate(live);
        if (!liveDate) return false;
        return isSameDay(liveDate, date);
      });
    },
    /** calendarDays와 동일한 그리드 범위 (보이는 주 단위) */
    getVisibleCalendarDayBounds() {
      const monthStart = startOfMonth(this.currentDate);
      const monthEnd = endOfMonth(this.currentDate);
      const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 });
      const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
      const startDay = new Date(
        calendarStart.getFullYear(),
        calendarStart.getMonth(),
        calendarStart.getDate()
      );
      const endDay = new Date(
        calendarEnd.getFullYear(),
        calendarEnd.getMonth(),
        calendarEnd.getDate()
      );
      return { startDay, endDay };
    },
    /** 문자열 / Date / Firestore Timestamp 등 — getEventsForDate와 동일 규칙 */
    parseLiveEventDate(live) {
      if (!live || live.date == null) return null;
      try {
        const d = live.date;
        if (typeof d === "string") {
          const tempDateStr = d.replace(/\./g, "-").trim();
          const formattedDateStr = !tempDateStr.includes(":")
            ? tempDateStr + " 00:00"
            : tempDateStr;
          const liveDate = parse(
            formattedDateStr,
            "yyyy-MM-dd HH:mm",
            new Date()
          );
          return isValid(liveDate) ? liveDate : null;
        }
        if (d instanceof Date) {
          return isValid(d) ? d : null;
        }
        if (typeof d.toDate === "function") {
          const t = d.toDate();
          return t && isValid(t) ? t : null;
        }
        if (typeof d.seconds === "number") {
          const t = new Date(d.seconds * 1000);
          return isValid(t) ? t : null;
        }
        const t = new Date(d);
        return isValid(t) ? t : null;
      } catch (error) {
        console.error("날짜 파싱 오류:", error, live.date);
        return null;
      }
    },
    isLiveInVisibleCalendarGrid(live) {
      const liveDate = this.parseLiveEventDate(live);
      if (!liveDate) return false;
      const dayStart = new Date(
        liveDate.getFullYear(),
        liveDate.getMonth(),
        liveDate.getDate()
      );
      const { startDay, endDay } = this.getVisibleCalendarDayBounds();
      return dayStart >= startDay && dayStart <= endDay;
    },
    async loadUserLikes() {
      if (!this.userStore.isLogin) {
        return;
      }

      try {
        const { getUserLikes } = await import("@/utils/read/user");
        const userLikes = await getUserLikes(this.userStore.email);
        this.userLiveLikes = userLikes.liveLikes || [];
      } catch (error) {
        console.error("사용자 찜 데이터 로드 오류:", error);
        this.userLiveLikes = [];
      }
    },
    async loadLikedLives() {
      if (!this.userStore.isLogin || this.userLiveLikes.length === 0) {
        this.likedLives = [];
        return;
      }

      try {
        const batchSize = 10;
        const livesData = [];

        for (let i = 0; i < this.userLiveLikes.length; i += batchSize) {
          const batch = this.userLiveLikes.slice(i, i + batchSize);

          const livesQuery = query(
            collection(firestore, "lives"),
            where(documentId(), "in", batch)
          );

          const livesSnapshot = await getDocs(livesQuery);

          const batchData = livesSnapshot.docs.map((doc) => {
            const liveData = doc.data();
            return {
              id: doc.id,
              title: liveData.title,
              date: liveData.date,
              place: liveData.place,
              type: liveData.type || "default",
              poster: null,
              _posterFile: liveData.img || null,
            };
          });

          livesData.push(...batchData);
        }

        this.likedLives = livesData;
        this.resolveLikedLivePosters();
      } catch (error) {
        console.error("찜한 공연 로드 오류:", error);
        this.likedLives = [];
      }
    },
    resolveLikedLivePosters() {
      this.likedLives.forEach(async (live) => {
        if (!live._posterFile || live.poster) return;
        if (!this.isLiveInVisibleCalendarGrid(live)) return;
        try {
          const posterRef = storageRef(storage, `poster/${live._posterFile}`);
          live.poster = await getDownloadURL(posterRef);
        } catch (error) {
          console.warn(`포스터 URL 생성 실패 (${live.id}, ${live._posterFile}):`, error);
        }
      });
    },
    parseEventDate(dateStr) {
      if (!dateStr) return null;
      const t = dateStr.replace(/\./g, '-').trim();
      const f = t.includes(':') ? t : t + ' 00:00';
      const d = parse(f, 'yyyy-MM-dd HH:mm', new Date());
      return isValid(d) ? d : null;
    },
    googleCalUrl(live) {
      const start = this.parseEventDate(live.date);
      if (!start) return '#';
      const end = new Date(start.getTime() + 2 * 60 * 60 * 1000);
      const fmt = (d) => format(d, "yyyyMMdd'T'HHmmss");
      const params = new URLSearchParams({
        action: 'TEMPLATE',
        text: live.title || '',
        dates: `${fmt(start)}/${fmt(end)}`,
        location: live.place || '',
        details: `링꾹에서 찜한 공연입니다.\nhttps://linkook.co.kr/live/${live.slug || live.id}`,
      });
      return `https://calendar.google.com/calendar/render?${params.toString()}`;
    },
    addAllToGoogleCal() {
      const urls = this.sortedLikedLives
        .map(live => this.googleCalUrl(live))
        .filter(u => u !== '#');
      if (!urls.length) return;
      urls.forEach((url, i) => {
        setTimeout(() => window.open(url, '_blank', 'noopener'), i * 300);
      });
    },
    exportToICS() {
      const lines = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//Linkook//MyCalendar//KO',
        'CALSCALE:GREGORIAN',
        'METHOD:PUBLISH',
      ];

      this.likedLives.forEach(live => {
        if (!live.date) return;
        try {
          const tempDateStr = live.date.replace(/\./g, '-').trim();
          const formattedDateStr = !tempDateStr.includes(':') ? tempDateStr + ' 00:00' : tempDateStr;
          const startDate = parse(formattedDateStr, 'yyyy-MM-dd HH:mm', new Date());
          if (!isValid(startDate)) return;

          const endDate = new Date(startDate.getTime() + 2 * 60 * 60 * 1000); // +2시간
          const fmt = (d) => format(d, "yyyyMMdd'T'HHmmss");
          const escape = (s) => (s || '').replace(/,/g, '\\,').replace(/;/g, '\\;').replace(/\n/g, '\\n');

          lines.push('BEGIN:VEVENT');
          lines.push(`UID:linkook-${live.id}@linkook.com`);
          lines.push(`DTSTART:${fmt(startDate)}`);
          lines.push(`DTEND:${fmt(endDate)}`);
          lines.push(`SUMMARY:${escape(live.title)}`);
          lines.push(`LOCATION:${escape(live.place)}`);
          lines.push('END:VEVENT');
        } catch (e) {
          console.error('ICS 변환 오류:', e, live.date);
        }
      });

      lines.push('END:VCALENDAR');

      const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = '내공연달력.ics';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    goToLiveDetail(liveId) {
      // 캘린더에서 공연 클릭 활동 기록
      trackActivity(ACTIVITY_TYPES.CALENDAR_LIVE_CLICK, {
        target: "live",
        targetId: liveId,
      });

      this.$router.push(`/live/${liveId}`);
    },
  },
};
</script>

<style module>
/* Medium: 481px ~ (기본 스타일, 최대 1024px) */
.container {
  width: 90%;
  margin: 0 auto;
  padding: 0 0 16px;
  font-family: "NanumSquareRoundR", sans-serif;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 52px;
  padding: 0 6px 0 4px;
  border-bottom: 1px solid #efefef;
  flex-shrink: 0;
}

.headerLeft {
  display: flex;
  align-items: center;
  gap: 4px;
}

.spacer {
  width: 36px;
}

/* 구글 캘린더 모달 */
.modalOverlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.modal {
  background: #fff;
  border-radius: 16px;
  width: 100%;
  max-width: 480px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  overflow: hidden;
}

.modalHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px 12px;
  border-bottom: 1px solid #f0f0f0;
}

.modalTitle {
  font-size: 1rem;
  font-weight: 700;
  color: #222;
  font-family: "NanumSquareRoundB", sans-serif;
}

.modalClose {
  background: none;
  border: none;
  font-size: 18px;
  color: #888;
  cursor: pointer;
  padding: 2px 6px;
  line-height: 1;
}

.modalClose:hover {
  color: #333;
}

.modalDesc {
  font-size: 0.8rem;
  color: #888;
  margin: 0;
  padding: 10px 20px 0;
  font-family: "NanumSquareRoundR", sans-serif;
}

.modalActions {
  padding: 10px 20px;
  border-bottom: 1px solid #f0f0f0;
}

.addAllBtn {
  width: 100%;
  padding: 9px;
  background: #4285F4;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 700;
  font-family: "NanumSquareRoundB", sans-serif;
  cursor: pointer;
  transition: background 0.2s;
}

.addAllBtn:hover {
  background: #2b6ed9;
}

.modalList {
  overflow-y: auto;
  flex: 1;
  padding: 8px 0;
}

.modalItem {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 20px;
  gap: 12px;
  border-bottom: 1px solid #f8f8f8;
}

.modalItem:last-child {
  border-bottom: none;
}

.modalItemInfo {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1;
}

.modalItemText {
  min-width: 0;
  flex: 1;
}

.typeBadge {
  font-size: 0.55rem;
  font-weight: 700;
  font-family: "NanumSquareRoundB", sans-serif;
  color: #fff;
  border-radius: 999px;
  padding: 2px 6px;
  flex-shrink: 0;
}

.modalItemTitle {
  font-size: 0.85rem;
  font-weight: 700;
  color: #222;
  font-family: "NanumSquareRoundB", sans-serif;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.modalItemMeta {
  font-size: 0.72rem;
  color: #888;
  font-family: "NanumSquareRoundR", sans-serif;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.addBtn {
  flex-shrink: 0;
  padding: 5px 12px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #333;
  font-family: "NanumSquareRoundB", sans-serif;
  text-decoration: none;
  transition: all 0.2s;
}

.addBtn:hover {
  border-color: #4285F4;
  color: #4285F4;
  background: rgba(66, 133, 244, 0.06);
}

.googleCalBtn {
  display: flex;
  align-items: center;
  gap: 5px;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: #333;
  font-family: "NanumSquareRoundB", sans-serif;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.googleCalBtn:hover {
  border-color: #66bdff;
  color: #2b8bd6;
  background: rgba(102, 189, 255, 0.12);
}

.backButton {
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 20px;
  color: #333;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  flex-shrink: 0;
  transition: background 0.15s;
  padding: 4px;
  transition: all 0.2s ease;
}

.backButton:hover {
  color: #66bdff;
  transform: translateX(-2px);
}

.title {
  font-size: 19px;
  font-weight: 700;
  color: #111;
  margin: 0;
  font-family: "NanumSquareRoundEB", sans-serif;
  letter-spacing: -0.3px;
}

.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #66bdff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.emptyState {
  text-align: center;
  padding: 60px 20px;
}

.emptyIcon {
  font-size: 64px;
  margin-bottom: 16px;
}

.emptyState h3 {
  font-size: 1.25rem;
  color: #333;
  margin: 0 0 8px 0;
  font-family: "NanumSquareRoundB", sans-serif;
}

.emptyState p {
  font-size: 0.875rem;
  color: #666;
  margin: 0;
}

.calendarContainer {
  background: white;
  border-radius: 12px;
  padding: 10px;
  padding-top: 0;
}

.calendarHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}

.navButton {
  border: none;
  background: transparent;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 20px;
  color: #333;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.navButton:hover {
  background: #e0e0e0;
}

.monthTitle {
  font-size: 1.25rem;
  font-weight: bold;
  color: #222;
  margin: 0;
  font-family: "NanumSquareRoundB", sans-serif;
}

.calendarWrapper {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.weekdayHeader {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0;
  padding: 6px 0 10px;
  border-bottom: 1px solid #e5e7eb;
}

.weekday {
  text-align: center;
  font-size: 0.8rem;
  font-weight: 600;
  color: #888;
  font-family: "NanumSquareRoundB", sans-serif;
}

.weekday:first-child {
  color: #ff5c5c;
}

.calendarGrid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0;
}

.calendarDay {
  height: 190px;
  border: none;
  border-bottom: 1px solid #f0f0f0;
  padding: 8px 4px 6px;
  cursor: pointer;
  transition: background 0.15s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background: #fff;
  overflow: hidden;
}

.calendarDay:hover {
  background: #f7f9ff;
}

.calendarDay.otherMonth {
  background: #fafafa;
}

.calendarDay.otherMonth .dayNumber {
  color: #ccc;
}

.calendarDay.sunday .dayNumber {
  color: #ff5c5c;
}

.calendarDay.sunday.otherMonth .dayNumber {
  color: #ffb3b3;
}

/* 날짜 숫자 래퍼 */
.dayNumberWrap {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  margin-bottom: 4px;
  flex-shrink: 0;
}

.dayNumberWrap.todayBadge {
  background: #1a1a1a;
}

.dayNumberWrap.todayBadge .dayNumber {
  color: #fff !important;
}

.dayNumber {
  font-size: 0.8rem;
  color: #333;
  font-weight: 600;
  font-family: "NanumSquareRoundB", sans-serif;
  line-height: 1;
}

/* 포스터 썸네일 */
.posterThumbs {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 2px;
  margin-bottom: 4px;
  width: 100%;
}

.posterThumb {
  width: 26px;
  height: 26px;
  min-width: 18px;
  border-radius: 30px;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-color: #e0e0e0;
}

.posterThumbEmpty {
  background-color: #d1d5db;
}

/* 이벤트 pill 목록 */
.calendarDayEvents {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow: hidden;
}

.calendarEventPill {
  width: 100%;
  font-size: 0.5rem;
  font-weight: 700;
  font-family: "NanumSquareRoundB", sans-serif;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  border-radius: 5px;
  padding: 2px 4px;
  line-height: 1.5;
}

/* 타입별 pill 색상 */
.eventType__vocal {
  background: #ff69b4;
}

.eventType__band {
  background: #66bdff;
}

.eventType__dance {
  background: #ffd84d;
  color: #7a5800;
}

.eventType__mix {
  background: linear-gradient(90deg, #66bdff, #ff69b4);
}

.eventType__default {
  background: #b0d8f5;
  color: #1a5f8a;
}

.selectedDateEvents {
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e0e0e0;
}

.selectedDateTitle {
  font-size: 1.125rem;
  font-weight: bold;
  color: #222;
  margin: 0 0 16px 0;
  font-family: "NanumSquareRoundB", sans-serif;
}

.eventsList {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
}

/* ========================================
   Small (~480px)
   ======================================== */
@media screen and (max-width: 480px) {
  .container {
    width: 100%;
    padding: 0 0 12px;
  }

  .header {
    padding: 0 6px 0 4px;
  }


  .monthTitle {
    font-size: 1rem;
  }

  .calendarContainer {
    padding: 0px;
  }

  .calendarDay {
    height: 130px;
    padding: 6px 2px 4px;
  }

  .posterThumb {
    width: 18px;
    height: 18px;
    border-radius: 25px;
  }

  .calendarEventPill {
    font-size: 0.5rem;
    padding: 1px 4px;
  }

  .dayNumber {
    font-size: 0.7rem;
  }

  .eventsList {
    grid-template-columns: repeat(1, 1fr);
    gap: 20px;
    padding: 10px 0;
  }
}
</style>
